import { Component, DoCheck, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { ErrorModalService } from 'app/core/modal/error-modal.service';
import { ConfirmModalService } from 'app/core/modal/confirm-modal.service';
import { ProgressBarDataModel } from '../progress-bar/progress-bar-data-model';
import { ActivityOperationType } from 'app/core/modal/activity-operation-type';
import { RequiredChildsData } from 'app/core/required-childs-data';
import { AutoSyncHistoryService } from './auto-sync-history.service';
import { catchError, debounceTime, Subject, Subscription } from 'rxjs';
import { UserSettingsInfoService } from 'app/settings/settings.service';
import { ScrollListenerDirective } from 'app/directives';

@Component({
	selector: 'app-auto-sync-history',
	templateUrl: './auto-sync-history.component.html',
	styleUrls: ['./auto-sync-history.component.scss'],
	standalone: false,
})
export class AutoSyncHistoryComponent implements OnInit, DoCheck, OnDestroy {
	loading = false;
	loadingInBody: boolean;
	historyItems: AutoSyncHistoryOperation[] = [];
	historyItemsDic: { [key: string]: AutoSyncHistoryOperation[] } = {};
	sortedHistoryItems: { key: string; value: AutoSyncHistoryOperation[] }[];
	compareDate: Date;
	pages: Array<number> = [];
	selectedPage = 1;
	progressBarData: ProgressBarDataModel;
	currentInventoryComponent: any;
	currentSyncInventoryOperation: ActivityOperationType;
	shopifyPage: number = 1;
	searchKeyword: string = '';
	toTopButtonVisible = false;
	pageButtonBlock: boolean;
	settings: any = {};
	autoSyncOperationStatusEnum = AutoSyncOperationStatus;

	public searchTrigger$: Subject<void> = new Subject<void>();
	private _searchTriggerSubscribe: Subscription;
	searchPlaceholder: string = 'Search by Name, Sku...';

	@Input() data: RequiredChildsData;

	constructor(
		private errorModalService: ErrorModalService,
		private confirmModalService: ConfirmModalService,
		private autoSyncHistoryService: AutoSyncHistoryService,
		private userSettingsService: UserSettingsInfoService
	) {
		this._searchTriggerSubscribe = this.searchTrigger$.pipe(debounceTime(1000)).subscribe(res => {
			this.getHistory(1);
		});
	}
	ngOnDestroy(): void {
		throw new Error('Method not implemented.');
	}

	async ngOnInit() {
		this.loading = true;
		let userSettings = await this.userSettingsService.getUserSettingsInfo();
		this.settings = userSettings;
		this.data.AutoReceiveApply = this.settings.AutoReceiveApply;
		this.data.AutoTransferApply = this.settings.AutoTransferApply;
		this.getHistory(1);
	}

	ngDoCheck() {}

	getHistory(page: number): void {
		this.shopifyPage = page;
		this.pageButtonBlock = false;

		this.loadingInBody = true;

		var request: AutoSyncFilter = {
			keyword: this.searchKeyword,
			page: page,
			//TODO: add switcher for type in the next iteration
			type: AutoSyncOperationType.Transfer,
		};

		this.autoSyncHistoryService
			.getHistory(request)
			.pipe(
				catchError(err => {
					console.log(err.message);
					this.errorModalService.open('Error', err.message);
					this.loadingInBody = false;
					this.loading = false;
					throw new Error(err.message);
				})
			)
			.subscribe(res => {
				this.historyItems = res.AutoSyncOperationModels;
				this.pageButtonBlock = this.searchKeyword != '' || !res.NextPage;
				this.loadingInBody = false;
				this.loading = false;
				this.historyItemsDic = this.historyItems.reduce<{
					[key: string]: AutoSyncHistoryOperation[];
				}>((acc, cur) => {
					let date = cur.CreateDate.toString();
					(acc[date] = acc[date] || []).push(cur);
					return acc;
				}, {});

				this.sortedHistoryItems = Object.keys(this.historyItemsDic)
					.sort((a, b) => {
						return new Date(b).getTime() - new Date(a).getTime();
					})
					.map(key => {
						return { key, value: this.historyItemsDic[key] };
					});
			});
	}

	refresh(): void {
		this.getHistory(1);
	}

	onContainerScroll(event: Event) {
		const target = event.target as HTMLElement;
		const scrollPosition = target.scrollTop;
		this.toTopButtonVisible = scrollPosition > 260;
	}

	scrollToTop(scrollableContainer: ScrollListenerDirective) {
		scrollableContainer.scrollToTop();
	}

	//   @HostListener("window:scroll", ["$event"])
	//   onScroll(event: Event) {
	//     const scrollPosition =
	//       document.documentElement.scrollTop || document.body.scrollTop;
	//     this.toTopButtonVisible = scrollPosition > 260;
	//   }

	//   topFunction() {
	//     document.body.scrollTop = 0;
	//     document.documentElement.scrollTop = 0;
	//   }

	public hasWaitingAdjustments(operation: any): boolean {
		return operation.ShopifyInventoryAdjustments?.some(
			(adj: any) => adj.Status === this.autoSyncOperationStatusEnum.Waiting
		);
	}

	public sync(item: ShopifyInventoryAdjustmentModel): void {
		this.loadingInBody = true;
		const ids = [item.Id];
		this.applySync(ids);
	}

	public syncAll(operation: AutoSyncHistoryOperation): void {
		this.loadingInBody = true;
		var ids: number[] = operation.ShopifyInventoryAdjustments.filter(
			o => o.Status == AutoSyncOperationStatus.Waiting
		).map(_ => _.Id);
		this.applySync(ids);
	}
	private applySync(ids: number[]) {
		this.autoSyncHistoryService
			.applyAutoSyncChanged(ids)
			.pipe(
				catchError(err => {
					console.log(err.message);
					this.errorModalService.open('Error', err.message);
					this.loadingInBody = false;
					this.loading = false;
					throw new Error(err.message);
				})
			)
			.subscribe(res => {
				this.setStatusAndQuantityOnHand(res);
				this.loadingInBody = false;
				this.loading = false;
			});
	}

	private setStatusAndQuantityOnHand(items: ShopifyUpdatedInventoryAdjustmentModel[]): void {
		items.forEach(item => {
			const targetItem = this.historyItems
				.flatMap(op => op.ShopifyInventoryAdjustments)
				.find(i => i.Id === item.Id);
			if (targetItem) {
				targetItem.Status = item.Status;
				targetItem.NewOnHandQuantity = item.NewOnHandQuantity;

				targetItem.ShopifyOriginInventoryAdjustments.forEach(o => {
					const originAdjustment = item.ShopifyOriginInventoryAdjustments.find(
						itemDetail => itemDetail.Id === o.Id
					);
					if (originAdjustment) {
						o.NewOnHandQuantity = originAdjustment.NewOnHandQuantity;
					}
				});
			}
		});
	}

	updateCheckbox() {
		this.userSettingsService
			.updateUserSettingsInfo(this.settings)
			.pipe(
				catchError(err => {
					console.log(err);
					this.errorModalService.open('Error', 'updating the settings');
					throw new Error(err.message);
				})
			)
			.subscribe(res => {
				this.data.AutoReceiveApply = this.settings.AutoReceiveApply;
				this.data.AutoTransferApply = this.settings.AutoTransferApply;
			});
	}
}

export interface AutoSyncFilter {
	type: AutoSyncOperationType;
	page: number;
	keyword?: string;
}

export interface AutoSyncOperationModelResponse {
	AutoSyncOperationModels: AutoSyncHistoryOperation[];
	Total: number;
	NextPage: boolean;
}

export interface AutoSyncHistoryOperation {
	Id: number;
	CreateDate: Date;
	Type: AutoSyncOperationType;
	PartnerAccountId: number;
	ShopifyInventoryAdjustments: ShopifyInventoryAdjustmentModel[];
}

export interface ShopifyInventoryAdjustmentModel {
	Id: number;
	AutoSyncOperationsId: number;
	InventoryItemId: number;
	DestinationLocationId: number;
	Adjustment: number;
	NewOnHandQuantity: number;
	ProductTitle: string;
	ImageUrl: string;
	ThumbnailUrl: string;
	LocationName: string;
	Status: AutoSyncOperationStatus;
	ShopifyOriginInventoryAdjustments: ShopifyOriginInventoryAdjustmentModel[];
}

export interface ShopifyOriginInventoryAdjustmentModel {
	Id: number;
	InventoryAdjustmentId: number;
	LocationId: number;
	Adjustment: number;
	NewOnHandQuantity: number;
	LocationName: string;
}

export interface ShopifyUpdatedInventoryAdjustmentModel {
	Id: number;
	AutoSyncOperationsId: number;
	InventoryItemId: number;
	DestinationLocationId: number;
	Adjustment: number;
	NewOnHandQuantity: number;
	Status: AutoSyncOperationStatus;
	ShopifyOriginInventoryAdjustments: ShopifyUpdatedOriginInventoryAdjustmentModel[];
}

export interface ShopifyUpdatedOriginInventoryAdjustmentModel {
	Id: number;
	InventoryAdjustmentId: number;
	LocationId: number;
	Adjustment: number;
	NewOnHandQuantity: number;
}
export interface AutoSyncOperationApplyModel {
	Id: number;
	Status: AutoSyncOperationStatus;
	Details: ShopifyInventoryAdjustmentModel[];
}

export interface AutoSyncOperationDetailsApplyModel {
	Id: number;
	NewOnHandQuantity: number;
}

export enum AutoSyncOperationStatus {
	Waiting = 'Waiting',
	Processing = 'Processing',
	Applied = 'Applied',
	Failed = 'Failed',
}

export enum AutoSyncOperationType {
	Purcahse = 'Purcahse',
	Transfer = 'Transfer',
}
